import { useAtomValue, useSetAtom } from "jotai";
import type { JSX } from "react";

import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Input } from "@frontend/components/atoms/Input";
import { useField } from "@frontend/hooks/form";
import { ClientField } from "@frontend/shared/client-field";
import { ReferenceField } from "@frontend/shared/reference-field";
import { useLingui } from "@lingui/react/macro";

import {
  loadClientScopeAtom,
  optionsAtom,
  projectEditorAtom,
  projectForm,
} from "../stores/editor";
import { Section } from "./Section";
import { SubTaskTypeField } from "./SubTaskTypeField";

const fields = projectForm.fields;

export function GeneralSection(): JSX.Element {
  const { t } = useLingui();

  const options = useAtomValue(optionsAtom);
  const { type, project } = useAtomValue(projectEditorAtom);
  const loadClientScope = useSetAtom(loadClientScopeAtom);

  const name = useField(fields.name);
  const voucherNumber = useField(fields.intervention.fields.voucherNumber);

  const intervention = "intervention" === type;

  return (
    <Section icon="Info" legend={t`General`}>
      <Field>
        <FieldLabel>{t`Name`}</FieldLabel>

        <FieldContent>
          <Input
            value={name.value ?? ""}
            placeholder={t`Name of the project`}
            onChange={(event) => name.setValue(event.target.value)}
          />

          <FieldError match={Boolean(name.error)}>{name.error}</FieldError>
        </FieldContent>
      </Field>

      <ClientField
        field={fields.clientId}
        fallbackLabel={project?.clientLabel}
        onClientSelected={(client) => {
          void loadClientScope(client?.id ?? null, client?.contactId ?? null);
        }}
      />

      {intervention ? null : (
        <ReferenceField
          label={t`Status`}
          placeholder={t`Please choose an option`}
          items={options.statuses}
          field={fields.statusId}
        />
      )}

      <ReferenceField
        label={t`Department`}
        placeholder={t`Please choose an option`}
        items={options.serviceGroups}
        field={fields.serviceGroupId}
      />

      {intervention ? (
        <>
          <ReferenceField
            label={t`Task type`}
            placeholder={t`Please choose an option`}
            items={options.taskTypes}
            field={fields.intervention.fields.taskTypeId}
          />

          <SubTaskTypeField />

          <Field>
            <FieldLabel>{t`Voucher number`}</FieldLabel>

            <FieldContent>
              <Input
                value={voucherNumber.value ?? ""}
                onChange={(event) => voucherNumber.setValue(event.target.value)}
              />
            </FieldContent>
          </Field>
        </>
      ) : null}
    </Section>
  );
}
